import React, { useEffect, useRef, useState } from "react";

import Hotjar from "@hotjar/browser";
import { useQuery } from "@tanstack/react-query";

import { UpsertDocScanCropsResponse, getUserDocumentScanById, listUserDocumentScanFields } from "services";

import { useNavigate, useTranslation, useUpsertDocScanCrops, useUserContext } from "utils";

import { COUNTRY_CODE, DOC_SCAN_REFRESH_INTERVAL, GET_DOC_SCAN } from "consts";

import { DocScanFieldRecord, DocScanValidationError } from "models";

import { Button, FullScreenOverlay, Icon, Modal, Spin, StickyFooterButtons, Tag, Typography, Message as message } from "components";

import { DateEntryStep } from "pages/app/document-scan/components/DateEntryStep";
import { ProcessingFacts } from "pages/app/document-scan/components/ProcessingFacts";
import { ReviewStep } from "pages/app/document-scan/components/ReviewStep";
import { SuccessStep } from "pages/app/document-scan/components/SuccessStep";
import { UploadStep } from "pages/app/document-scan/components/UploadStep";
import { buildUpsertDocScanCropField, isEligibleForDateEntry } from "pages/app/document-scan/utils/dateEntryUtils";
import { getOverallStatusCount } from "pages/app/document-scan/utils/docScanStats";

const trackEvent = (event: string) => {
  if (Hotjar.isReady()) {
    Hotjar.event(event);
  }
};

type Step = "upload" | "processing" | "review_matched" | "review_unclear" | "date_entry" | "success";

type SuccessData = { year: number; fieldsMatched: number; inserted: number };

const isValidationError = (data: unknown): data is DocScanValidationError => data !== null && typeof data === "object" && "error" in data;

export const DocumentScanOverlay = ({ showOverlay = false, onClose }: { showOverlay: boolean; onClose: () => void }) => {
  const { t } = useTranslation("docScans");
  const navigate = useNavigate();
  const { user } = useUserContext();
  const isPoland = user?.farm?.country === COUNTRY_CODE.POLAND;

  const [step, setStep] = useState<Step>("upload");
  const [docScanId, setDocScanId] = useState<string | null>(null);

  const [serverError, setServerError] = useState<string | undefined>(undefined);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [matchOverrides, setMatchOverrides] = useState<Map<string, DocScanFieldRecord>>(new Map());
  const [selectedFieldIds, setSelectedFieldIds] = useState<Set<string>>(new Set());
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState(false);
  const [fieldDates, setFieldDates] = useState<Map<string, { seedingStart: string; seedingEnd: string; harvestStart: string; harvestEnd: string }>>(new Map());
  const [successData, setSuccessData] = useState<SuccessData | null>(null);
  const [eligibleRowsForDateEntry, setEligibleRowsForDateEntry] = useState<DocScanFieldRecord[]>([]);
  const [dateEntryYear, setDateEntryYear] = useState<number | undefined>(undefined);
  const seenFieldRecords = useRef<Map<string, DocScanFieldRecord>>(new Map());
  const selectAllActiveRef = useRef(true);
  const contentRef = useRef<HTMLDivElement>(null);

  const { mutateAsync: upsertCrops } = useUpsertDocScanCrops();

  const { data: docScan, isError: isDocScanError } = useQuery({
    queryKey: [GET_DOC_SCAN, docScanId],
    queryFn: () => getUserDocumentScanById(docScanId!),
    enabled: !!docScanId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "processing" || status === "pending") {
        return DOC_SCAN_REFRESH_INTERVAL;
      }
      return false;
    },
  });

  // `showToast: false` is for failures the axios interceptor has already toasted, so we don't stack two.
  const failBackToUpload = (errorMessage: string, { showToast = true }: { showToast?: boolean } = {}) => {
    if (showToast) {
      message({ content: errorMessage, icon: "error" });
    }

    setServerError(errorMessage);
    setDocScanId(null);
    setStep("upload");
  };

  useEffect(() => {
    if (!docScan || step !== "processing") return;

    if (docScan.status === "completed") {
      // The review steps only ever list match and need_review records, so anything else leaves nothing to select.
      const completedMatchCount = getOverallStatusCount(docScan, "match");
      const completedNeedReviewCount = getOverallStatusCount(docScan, "need_review");

      if (completedMatchCount + completedNeedReviewCount === 0) {
        failBackToUpload(t("upload.errors.noFieldData"));
        return;
      }

      if (!isPoland && completedMatchCount === 0) {
        selectAllActiveRef.current = false;
        setStep("review_unclear");
      } else {
        setStep("review_matched");
      }
      return;
    }

    if (docScan.status !== "failed") return;

    let errorMessage = t("processing.errors.generic");

    if (isValidationError(docScan.data)) {
      const { code } = docScan.data.error;

      if (code === "YEAR_MISMATCH") {
        errorMessage = t("processing.errors.yearMismatch", {
          documentYear: docScan.data.error.document_year,
          expectedYear: docScan.data.error.expected_year,
        });
      }
    }

    failBackToUpload(errorMessage);
  }, [docScan?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Without this the farmer would sit on the processing spinner forever whenever polling gives up.
  useEffect(() => {
    if (!isDocScanError || step !== "processing") return;

    failBackToUpload(t("processing.errors.generic"), { showToast: false });
  }, [isDocScanError, step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showOverlay) {
      trackEvent("doc_scan_opened");
    }
  }, [showOverlay]);

  useEffect(() => {
    if (!showOverlay || step === "upload") return;
    const stepEventMap: Record<Step, string> = {
      upload: "doc_scan_step_upload",
      processing: "doc_scan_step_processing",
      review_matched: "doc_scan_step_review_matched",
      review_unclear: "doc_scan_step_review_unclear",
      date_entry: "doc_scan_step_date_entry",
      success: "doc_scan_completed",
    };
    trackEvent(stepEventMap[step]);
  }, [step, showOverlay]);

  // Every step reuses the same scroll container, so the browser carries the previous step's
  // scrollTop over. Reset it (plus the nested/ancestor scrollers) so each page starts at the top.
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    content.scrollTop = 0;

    // date_entry hides the content div's own overflow — its scroller is antd's table body.
    const tableBody = content.querySelector<HTMLElement>(".ant-table-body");
    if (tableBody) tableBody.scrollTop = 0;

    // On phone widths the antd modal wrap scrolls too.
    const modalWrap = content.closest<HTMLElement>(".ant-modal-wrap");
    if (modalWrap) modalWrap.scrollTop = 0;
  }, [step]);

  const shouldBlock = step !== "upload" && step !== "success";
  const hasFooter = step === "review_matched" || step === "review_unclear" || step === "date_entry";

  useEffect(() => {
    if (!shouldBlock) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [shouldBlock]);

  const handleCloseClick = () => {
    if (shouldBlock) {
      setShowCloseConfirm(true);
    } else {
      trackEvent("doc_scan_closed");
      onClose?.();
    }
  };

  const handleCloseConfirm = () => {
    setShowCloseConfirm(false);
    trackEvent("doc_scan_closed");
    onClose?.();
  };

  const needReviewCount = getOverallStatusCount(docScan, "need_review");

  const fetchRemainingMatchFields = async (): Promise<Set<string>> => {
    if (!docScanId || !docScan) return new Set(selectedFieldIds);
    const totalMatchCount = getOverallStatusCount(docScan, "match");
    const seenMatchIds = new Set([...seenFieldRecords.current.entries()].filter(([, r]) => r.overallStatus === "match").map(([id]) => id));

    if (seenMatchIds.size < totalMatchCount) {
      let page = Math.ceil(seenMatchIds.size / 100) + 1;
      while (seenMatchIds.size < totalMatchCount) {
        // eslint-disable-next-line no-await-in-loop
        const result = await listUserDocumentScanFields({ id: docScanId, page, limit: 100, sortBy: "createdAt", sortOrder: "desc", overallStatus: "match" });
        result.fields.forEach((f) => {
          if (!seenFieldRecords.current.has(f.id)) {
            seenFieldRecords.current.set(f.id, f);
            seenMatchIds.add(f.id);
          }
        });
        if (result.fields.length < 100) break;
        page += 1;
      }
    }

    setSelectedFieldIds((prev) => {
      const next = new Set(prev);
      if (selectAllActiveRef.current) seenMatchIds.forEach((id) => next.add(id));
      return next;
    });

    const effective = new Set(selectedFieldIds);
    if (selectAllActiveRef.current) seenMatchIds.forEach((id) => effective.add(id));
    return effective;
  };

  const matchCount = getOverallStatusCount(docScan, "match");

  const handleBack = () => {
    setServerError(undefined);

    if (step === "date_entry") {
      setStep(!isPoland && needReviewCount > 0 ? "review_unclear" : "review_matched");
      return;
    }
    // TODO: think about handling the steps by adding/subtracting?
    if (step === "review_unclear") {
      if (matchCount === 0) {
        setStep("upload");
        setDocScanId(null);
      } else {
        setStep("review_matched");
      }
    } else {
      setStep("upload");
      setDocScanId(null);
    }
  };

  const handleContinue = async () => {
    if (step === "review_matched") {
      if (!isPoland && needReviewCount > 0) {
        await fetchRemainingMatchFields();
        selectAllActiveRef.current = false;
        setStep("review_unclear");
        return;
      }
    }

    if (step === "review_matched" || step === "review_unclear") {
      const effectiveSelectedIds = step === "review_matched" ? await fetchRemainingMatchFields() : selectedFieldIds;
      const merged = new Map(seenFieldRecords.current);
      matchOverrides.forEach((r, id) => merged.set(id, r));
      const eligibleRows = [...merged.values()].filter((r) => effectiveSelectedIds.has(r.id) && isEligibleForDateEntry(r));
      setEligibleRowsForDateEntry(eligibleRows);
      setDateEntryYear(docScan?.year ?? undefined);
      setStep("date_entry");
      return;
    }

    if (!docScanId) return;

    setConfirmError(false);
    setIsConfirming(true);

    try {
      const fields = eligibleRowsForDateEntry.filter((r) => fieldDates.has(r.id)).map((r) => buildUpsertDocScanCropField(r, fieldDates, dateEntryYear!));

      const result: UpsertDocScanCropsResponse = await upsertCrops({ docScanId, payload: { fields } });
      setSuccessData({
        year: dateEntryYear ?? new Date().getFullYear(),
        fieldsMatched: fields.length,
        inserted: result.inserted,
      });
      setStep("success");
    } catch {
      setConfirmError(true);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleAddMissingData = () => {
    onClose();
    navigate("/all-fields");
  };

  const handleImportMore = () => {
    setStep("upload");
    setServerError(undefined);
    setDocScanId(null);
    setMatchOverrides(new Map());
    setSelectedFieldIds(new Set());
    setFieldDates(new Map());
    setSuccessData(null);
    setEligibleRowsForDateEntry([]);
    setDateEntryYear(undefined);
    seenFieldRecords.current = new Map();
    selectAllActiveRef.current = true;
  };

  const handleUploadSuccess = (id: string) => {
    setServerError(undefined);
    setDocScanId(id);
    setStep("processing");
  };

  const anyDateFilled = eligibleRowsForDateEntry.length > 0 && eligibleRowsForDateEntry.some((r) => fieldDates.has(r.id));

  // PL fields are never left ambiguous server-side, so the "review unclear" step never applies to them.
  const steps = [
    { label: t("upload.steps.upload"), active: step === "upload" || step === "processing" },
    { label: t("upload.steps.reviewMatched"), active: step === "review_matched" },
    ...(isPoland ? [] : [{ label: t("upload.steps.reviewUnclear"), active: step === "review_unclear" }]),
    { label: t("upload.steps.dateEntry"), active: step === "date_entry" },
  ].map((s, index) => ({ ...s, number: index + 1 }));

  return (
    <FullScreenOverlay showModal={showOverlay} onClose={handleCloseClick} wrapClassName="full-screen-overlay doc-scan-overlay" dataTestId="document-scan-page">
      <div className="doc-scan-custom-header">
        <div className="doc-scan-header-left">
          <Typography variant="h4">{t("upload.overlayTitle")}</Typography>
          <Tag colour="yellow" text={t("upload.newBadge")} size="small" />
        </div>

        <div className="doc-scan-stepper">
          {step !== "success" &&
            steps.map((s, index) => (
              <React.Fragment key={s.number}>
                <div className={`doc-scan-stepper-circle ${s.active ? "active" : ""}`}>
                  <span>{s.number}</span>
                </div>
                <Typography variant="paragraph-s" color={s.active ? undefined : "grey-600"}>
                  {s.label}
                </Typography>
                {index < steps.length - 1 && <div className="doc-scan-stepper-line" />}
              </React.Fragment>
            ))}
        </div>

        <div className="doc-scan-header-right">
          <Icon iconName="tabler/x" size="m" color="grey-600" className="pointer" onClick={handleCloseClick} dataTestId="doc-scan-close" />
        </div>
      </div>

      <div
        ref={contentRef}
        data-testid="doc-scan-overlay-content"
        className={`doc-scan-overlay-content${hasFooter ? "" : " no-footer"}`}
        style={step === "date_entry" ? { display: "flex", flexDirection: "column", overflowY: "hidden" } : undefined}
      >
        {step === "processing" && (
          <div className="doc-scan-processing">
            <Spin iconName="tabler/loader" />
            <Typography variant="h2" className="margin-top-150">
              {t("processing.title")}
            </Typography>
            <Typography variant="paragraph-m" className="grey-text margin-top-100">
              {t("processing.subtitle")}
            </Typography>
            <ProcessingFacts />
          </div>
        )}

        {step === "upload" && <UploadStep onUploadSuccess={handleUploadSuccess} serverError={serverError} />}

        {step.includes("review") && docScan && (
          <ReviewStep
            key={step}
            docScan={docScan}
            mode={step === "review_matched" ? "match" : "need_review"}
            selectedFieldIds={selectedFieldIds}
            onSelectedFieldIdsChange={setSelectedFieldIds}
            matchOverrides={matchOverrides}
            onMatchOverridesChange={setMatchOverrides}
            seenFieldRecords={seenFieldRecords}
            selectAllActiveRef={selectAllActiveRef}
            isPoland={isPoland}
          />
        )}

        {step === "date_entry" && (
          <DateEntryStep eligibleRows={eligibleRowsForDateEntry} selectedYear={dateEntryYear} fieldDates={fieldDates} onFieldDatesChange={setFieldDates} />
        )}

        {step === "success" && successData && (
          <SuccessStep
            year={successData.year}
            fieldsMatched={successData.fieldsMatched}
            inserted={successData.inserted}
            onAddMissingData={handleAddMissingData}
            onImportMore={handleImportMore}
          />
        )}
      </div>

      {(step === "review_matched" || step === "review_unclear") && (
        <StickyFooterButtons handleBack={handleBack} className="doc-scan-overlay-footer">
          {confirmError && (
            <Typography variant="paragraph-s" color="red-500">
              {t("upsertCrops.error")}
            </Typography>
          )}
          <Button type="primary" dataTestId="doc-scan-continue" onClick={handleContinue} loading={isConfirming} disabled={isConfirming}>
            {t("upload.footer.continue")}
          </Button>
        </StickyFooterButtons>
      )}

      {step === "date_entry" && (
        <StickyFooterButtons handleBack={handleBack} className="doc-scan-overlay-footer">
          {confirmError && (
            <Typography variant="paragraph-s" color="red-500">
              {t("upsertCrops.error")}
            </Typography>
          )}
          <Button type="primary" dataTestId="date-entry-submit" onClick={handleContinue} loading={isConfirming} disabled={!anyDateFilled || isConfirming}>
            {t("upload.footer.importCrops")}
          </Button>
        </StickyFooterButtons>
      )}
      <Modal
        dataTestId="doc-scan-close-confirm"
        showModal={showCloseConfirm}
        title={t("upload.closeConfirm.title")}
        description={t("upload.closeConfirm.description")}
        okText={t("upload.closeConfirm.confirm")}
        cancelText={t("upload.closeConfirm.cancel")}
        onOk={handleCloseConfirm}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </FullScreenOverlay>
  );
};
